import { Fragment, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faColumns, faFileExport } from '@fortawesome/free-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import { FeatureKey, ModalEnum, PARKING_SESSIONS_TAB } from 'myParkdUtils/enums';
import * as modalWindowActions from 'root/modal/modalWindowActions';
import moment from 'moment';
import AppWrapper from 'components/board/AppWrapper';
import GlobalErrorBoundary from 'components/GlobalErrorBoundary';
import ParkingSessionsView from 'screens/parkingSessions/ParkingSessionsView';
import { PagingReactTable, ReduxState } from 'types';
import MainHeader from 'components/board/MainHeader';
import MainContent from 'components/board/MainContent';
import { ParkingSessionsColorLegend } from './ParkingSessionsColorLegend';
import { ColumnVisibility, OptionalColumn } from 'screens/modals/ManageTableColumnsModal';
import { getMyParkdUserSettings } from 'features/settings/myParkdSettings';
import { ColumnFilter, ColumnSort } from '@tanstack/react-table';
import { DateRange } from 'react-day-picker';
import DateRangePickerInline from 'components/DateRangePickerInline';
import { getFirstDayOfCurrentMonth } from 'myParkdUtils/dateRangePickerUtils';
import 'react-day-picker/style.css';
import { activeSessionsSliceActions } from 'features/parkingsessions/activeSessionsSlice';
import { defaultSortingOnstreetSessions, historicalSessionsSliceActions } from 'features/parkingsessions/historicalSessionsSlice';
import { defaultSortingOffstreetSessions, offstreetSessionsSliceActions } from 'features/parkingsessions/offstreetSessionsSlice';
import PermissionGate from 'components/PermissionGate';


export const DEFAULT_COLUMNS_HISTORICAL_SESSIONS: ColumnVisibility = {
    'licensePlate': true,
    'cost': true,
    'zonecode': true,
    'location': true,
    'duration': true,
    'date': true,
    'sessionAnnotation.sessionNote': false,
    'sessionAnnotation.costCenter': false,
    'email': false
}

export const DEFAULT_COLUMNS_OFFSTREET_SESSIONS: ColumnVisibility = {
    'licensePlate': true,
    'cost': true,
    'location': true,
    'date': true,
    'sessionAnnotation.costCenter': false,
    'email': false
}

export const OPTIONAL_COLUMNS_HISTORICAL_SESSIONS: OptionalColumn[] = [
    { accessorKey: 'sessionAnnotation.sessionNote', displayNameId: 'DATA_TABLE_NOTE' },
    { accessorKey: 'sessionAnnotation.costCenter', displayNameId: 'DATA_TABLE_COST_CENTER' },
    { accessorKey: 'email', displayNameId: 'DATA_TABLE_DRIVER_EMAIL' }
]

export const OPTIONAL_COLUMNS_OFFSTREET_SESSIONS: OptionalColumn[] = [
    { accessorKey: 'sessionAnnotation.costCenter', displayNameId: 'DATA_TABLE_COST_CENTER' },
    { accessorKey: 'email', displayNameId: 'DATA_TABLE_DRIVER_EMAIL' },
    { accessorKey: 'user', displayNameId: 'DATA_TABLE_DRIVER_NAME' }
]


/*
export const defaultSortingOnstreetSessions: ColumnSort[] = [{ id: 'startTime', desc: true }];
export const defaultSortingOffstreetSessions: ColumnSort[] = [{ id: 'endTime', desc: true }];
*/


export const ParkingSessionsScreen = (props: Props) => {

    const { TAB_HISTORICAL, TAB_ACTIVE, TAB_OFFSTREET } = PARKING_SESSIONS_TAB;

    const today = new Date();

    const [openedTab, setOpenedTab] = useState<PARKING_SESSIONS_TAB>(TAB_HISTORICAL);
    const [dateRange, setDateRange] = useState<DateRange>({ from: getFirstDayOfCurrentMonth(), to: today });

    useEffect(() => {
        getActiveSessions([{ id: 'licensePlate', value: '' }], { currentPage: 0, pageSize: 100 }, defaultSortingOnstreetSessions);
    }, []);

    const exportParkingHistory = () => {
        props.openModalWindow(ModalEnum.EXPORT_HISTORY, {
            tableFilters: props.exportFilters,
            openedTab
        });
    };

    const getActiveSessions = (filters: ColumnFilter[], paging: PagingReactTable, sorting: ColumnSort[]) => {
        props.getActiveSessions({ filters, paging, sorting });
    };

    const getHistoricalSessions = (filters: ColumnFilter[], paging: PagingReactTable, sorting: ColumnSort[]) => {
        props.getHistoricalSessions({ filters, paging, sorting });
    };

    const getOffstreetSessions = (filters: ColumnFilter[], paging: PagingReactTable, sorting: ColumnSort[]) => {
        props.getOffstreetSessions({ filters, paging, sorting });
    }

    const tableSettingsName = openedTab === TAB_HISTORICAL
        ? 'parkingSessionsColumnSettings'
        : 'parkingSessionsOffstreetColumnSettings';

    const optionalColumns = openedTab === TAB_HISTORICAL
        ? OPTIONAL_COLUMNS_HISTORICAL_SESSIONS
        : OPTIONAL_COLUMNS_OFFSTREET_SESSIONS;

    return (
        <AppWrapper>
            <main id="page-parking">

                <MainHeader>
                    <div className="header-v-align">
                        <h2 className="main-header-title">
                            <FormattedMessage id="FILTER_SESSIONS" />
                        </h2>


                        {(openedTab === TAB_HISTORICAL || openedTab === TAB_OFFSTREET) && (
                            <Fragment>
                                <DateRangePickerInline
                                    startDate={dateRange.from!}
                                    endDate={dateRange.to!}
                                    numberOfMonths={2}
                                    onDateRangeChanged={(range) => setDateRange(range)}
                                />

                                <div className='header-action-buttons'>
                                    <button className='btn-small-blue'
                                        onClick={() => props.openModalWindow(ModalEnum.MANAGE_TABLE_COLUMNS, {
                                            tableSettingsName,
                                            optionalColumns
                                        })}
                                    >
                                        <FontAwesomeIcon icon={faColumns} className='fa-icon' />
                                        <FormattedMessage id="PARKING_HISTORY_BTN_TABLE_SETTINGS" />
                                    </button>
                                    <button className="btn-small-blue" onClick={() => exportParkingHistory()}>
                                        <FontAwesomeIcon icon={faFileExport} className="fa-icon" />
                                        <FormattedMessage id="GLOBAL_EXPORT" />
                                    </button>
                                </div>
                            </Fragment>
                        )}
                    </div>
                </MainHeader>

                <MainContent>
                    <GlobalErrorBoundary className="eb-red-background eb-rounded-corners">
                        <div className="board-content radius-rectangle no-overflow">

                            <div className="tabs-holder">
                                <div
                                    id="tab-historical-sessions"
                                    data-testid="tab-historical-sessions"
                                    className={'tab' + (openedTab === TAB_HISTORICAL ? ' active' : '')}
                                    onClick={() => setOpenedTab(TAB_HISTORICAL)}
                                >
                                    <span><FormattedMessage id="TAB_STOPPED_SESSIONS" /></span>
                                </div>
                                <div
                                    id="tab-active-sessions"
                                    data-testid="tab-active-sessions"
                                    className={'tab' + (openedTab === TAB_ACTIVE ? ' active' : '')}
                                    onClick={() => setOpenedTab(TAB_ACTIVE)}
                                >
                                    <FormattedMessage id="TAB_ACTIVE_SESSIONS" />
                                    &nbsp;({props.activeSessions.paging.totalElements})
                                </div>
                                <PermissionGate featureKey={FeatureKey.PARKD_CARDS}>
                                    <div
                                        id="tab-offstreet-sessions"
                                        data-testid="tab-offstreet-sessions"
                                        className={'tab' + (openedTab === TAB_OFFSTREET ? ' active' : '')}
                                        onClick={() => setOpenedTab(TAB_OFFSTREET)}
                                    >
                                        <FormattedMessage id="TAB_OFFSTREET_SESSIONS" />
                                    </div>
                                </PermissionGate>
                            </div>

                            {openedTab === TAB_ACTIVE && (
                                <ParkingSessionsView
                                    sessions={props.activeSessions.sessions}
                                    updateSessions={getActiveSessions}
                                    token={props.token!}
                                    openModalWindow={props.openModalWindow}
                                    status={props.activeSessions.status}
                                    paging={props.activeSessions.paging}
                                    userLocale={props.userLocale!}
                                    openedTab={openedTab}
                                    defaultSorted={defaultSortingOnstreetSessions}
                                />
                            )}

                            {openedTab === TAB_HISTORICAL && (
                                <ParkingSessionsView
                                    sessions={props.historicalSessions.sessions}
                                    updateSessions={getHistoricalSessions}
                                    dateFrom={moment(dateRange.from).format('YYYY-MM-DD') + 'T00:00:00'}
                                    dateTo={moment(dateRange.to).format('YYYY-MM-DD') + 'T23:59:59'}
                                    status={props.historicalSessions.status}
                                    paging={props.historicalSessions.paging}
                                    userLocale={props.userLocale!}
                                    openedTab={openedTab}
                                    defaultSorted={defaultSortingOnstreetSessions}
                                    parkingSessionsColumnSettings={props.myParkdUserSettings?.parkingSessionsColumnSettings}
                                />
                            )}

                            {openedTab === TAB_OFFSTREET && (
                                <>
                                    <ParkingSessionsView
                                        sessions={props.offstreetSessions.sessions}
                                        updateSessions={getOffstreetSessions}
                                        dateFrom={moment(dateRange.from).format('YYYY-MM-DD') + 'T00:00:00'}
                                        dateTo={moment(dateRange.to).format('YYYY-MM-DD') + 'T23:59:59'}
                                        status={props.offstreetSessions.status}
                                        paging={props.offstreetSessions.paging}
                                        userLocale={props.userLocale!}
                                        openedTab={openedTab}
                                        defaultSorted={defaultSortingOffstreetSessions}
                                        parkingSessionsColumnSettings={props.myParkdUserSettings?.parkingSessionsOffstreetColumnSettings}
                                    />
                                </>
                            )}

                        </div>

                        <ParkingSessionsColorLegend />

                    </GlobalErrorBoundary>
                </MainContent>

            </main>
        </AppWrapper >
    );
};


type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = StateProps & DispatchProps;


function mapStateToProps(state: ReduxState) {
    return {
        historicalSessions: state.historicalSessions,
        activeSessions: state.activeSessions,
        offstreetSessions: state.offstreetSessions,
        exportFilters: state.historicalSessions.exportFilters,
        exportSorting: state.historicalSessions.exportSorting,
        userLocale: state.user?.user?.locale,
        token: state.auth.token,
        myParkdUserSettings: getMyParkdUserSettings(state)
    };
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
    return bindActionCreators(
        {
            getHistoricalSessions: historicalSessionsSliceActions.getHistoricalSessions,
            getActiveSessions: activeSessionsSliceActions.getActiveSessions,
            getOffstreetSessions: offstreetSessionsSliceActions.getOffstreetSessions,
            openModalWindow: modalWindowActions.updateModalWindow,
        },
        dispatch
    );
}

export default connect(mapStateToProps, mapDispatchToProps)(ParkingSessionsScreen);